import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, MapPin, Package } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Shipment } from "../../types";

interface SortableShipmentCardProps {
  shipment: Shipment;
  isReadOnly?: boolean;
  order?: number;
}

const SortableShipmentCard: React.FC<SortableShipmentCardProps> = ({
  shipment,
  isReadOnly = false,
  order,
}) => {
  const { t } = useTranslation("shipmentGroup");

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: shipment._id,
    disabled: isReadOnly,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const typeColors = {
    Domestic: "bg-blue-50 text-blue-600 border border-blue-200",
    International: "bg-purple-50 text-purple-600 border border-purple-200",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border border-gray-300 rounded-lg p-3 ${
        isDragging ? "shadow-lg z-50" : "shadow-sm"
      } ${!isReadOnly ? "cursor-move hover:border-blue-400" : ""}`}
    >
      <div className="flex items-start space-x-3">
        {/* Drag Handle */}
        {!isReadOnly && (
          <div
            {...attributes}
            {...listeners}
            className="shrink-0 pt-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="h-5 w-5" />
          </div>
        )}

        {/* Order Badge */}
        {order !== undefined && (
          <div className="shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold">
            {order}
          </div>
        )}

        {/* Shipment Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h4 className="font-medium text-gray-900">
              {shipment.shipmentNumber}
              {shipment.referenceNumber && (
                <span className="text-gray-500 text-sm ml-2">
                  ({shipment.referenceNumber})
                </span>
              )}
            </h4>
            {shipment.type && (
              <span
                className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                  typeColors[shipment.type as keyof typeof typeColors] ||
                  "bg-gray-50 text-gray-600"
                }`}
              >
                {t(`card.typeLabels.${shipment.type}`, {
                  defaultValue: shipment.type,
                })}
              </span>
            )}
            <span
              className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                shipment.status === "ReadyForPickup"
                  ? "bg-purple-100 text-purple-800"
                  : shipment.status === "Order"
                    ? "bg-gray-100 text-gray-800"
                    : shipment.status === "AwaitingPickup"
                      ? "bg-orange-100 text-orange-800"
                      : shipment.status === "InTransit"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-green-100 text-green-800"
              }`}
            >
              {shipment.status === "AwaitingPickup"
                ? t("card.status.AwaitingPickup")
                : shipment.status}
            </span>
          </div>

          {/* Pickup */}
          <div className="flex items-start space-x-2 mb-1">
            <MapPin className="h-3.5 w-3.5 text-green-600 mt-0.5 shrink-0" />
            <div className="text-xs text-gray-600 truncate">
              <span className="font-medium">{t("card.from")}</span>{" "}
              {shipment?.pickup?.companyName || "-"}
              {shipment?.pickup?.address?.city &&
                ` - ${shipment.pickup.address.city}`}
            </div>
          </div>

          {/* Delivery */}
          <div className="flex items-start space-x-2 mb-1">
            <MapPin className="h-3.5 w-3.5 text-red-600 mt-0.5 shrink-0" />
            <div className="text-xs text-gray-600 truncate">
              <span className="font-medium">{t("card.to")}</span>{" "}
              {shipment.delivery?.companyName || "-"}
              {shipment.delivery?.address?.city &&
                ` - ${shipment.delivery.address.city}`}
            </div>
          </div>

          {/* Cargo Info */}
          <div className="flex items-center space-x-2 mt-2 text-xs text-gray-500">
            <Package className="h-3.5 w-3.5" />
            <span>
              {shipment.totalQuantity || 0} {t("card.items")}
            </span>
            <span className="ml-2">
              • {shipment.totalWeight || 0} {t("table.cell.weightUnit")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SortableShipmentCard;
